import { theme } from '@resistapp/client/components/shared/theme';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import { useId } from 'react';

type Props = {
  width: number;
  height: number;
  data: Array<{ x: number; y: number }>; // x = mean Cq, y = delta Cq
  meanDelta: number;
  sdDelta: number;
  loaLower: number;
  loaUpper: number;
};

export function BlandAltmanPlot({ width, height, data, meanDelta, sdDelta, loaLower, loaUpper }: Props) {
  const margin = { top: 10, right: 20, bottom: 40, left: 50 };
  const innerWidth = Math.max(0, width - margin.left - margin.right);
  const innerHeight = Math.max(0, height - margin.top - margin.bottom);

  const platformQaLodCt = 28;
  const clipIdRaw = useId();
  const clipIdSafe = clipIdRaw.replace(/:/g, '');
  const clipPathId = `bland-altman-clip-${clipIdSafe}`;

  const xs = data.map(d => d.x).filter(Number.isFinite);
  const ys = data.map(d => d.y).filter(Number.isFinite);

  const xMin = xs.length ? Math.min(...xs) : 0;
  const xMax = xs.length ? Math.max(...xs) : 1;
  const yMinData = ys.length ? Math.min(...ys) : -1;
  const yMaxData = ys.length ? Math.max(...ys) : 1;
  const yMin = Math.min(yMinData, loaLower, meanDelta - sdDelta) - 0.2;
  const yMax = Math.max(yMaxData, loaUpper, meanDelta + sdDelta) + 0.2;

  const xScale = scaleLinear<number>({ domain: [xMin, xMax], range: [0, innerWidth] });
  const yScale = scaleLinear<number>({ domain: [yMin, yMax], range: [innerHeight, 0] });

  const xLabelPos = xScale(xMin + 0.94 * (xMax - xMin));
  const showPlatformQaLod = xMin <= platformQaLodCt && platformQaLodCt <= xMax;

  const plottedPoints = data.map(d => {
    const ctA = d.x + d.y / 2;
    const ctB = d.x - d.y / 2;
    const aBelowQaLod = ctA >= platformQaLodCt;
    const bBelowQaLod = ctB >= platformQaLodCt;

    if (aBelowQaLod && bBelowQaLod) {
      return { ...d, r: 2.2, fill: theme.colors.neutral500, fillOpacity: 0.55 };
    }
    if (aBelowQaLod || bBelowQaLod) {
      return { ...d, r: 2.8, fill: theme.colors.riskScore100, fillOpacity: 0.85 };
    }
    return { ...d, r: 2.5, fill: theme.colors.neutral900, fillOpacity: 0.9 };
  });

  return (
    <svg width={width} height={height}>
      <defs>
        <clipPath id={clipPathId}>
          <rect x={0} y={0} width={innerWidth} height={innerHeight} rx={8} ry={8} />
        </clipPath>
      </defs>
      <Group left={margin.left} top={margin.top}>
        <g clipPath={`url(#${clipPathId})`}>
          {/* platform QA LOQ reference line on the mean axis (Cq 28). Clean non-detections are imputed to this value,
              so points are coloured by which side of it each Cq sits — no diagonal LOD wedge is needed. */}
          {showPlatformQaLod && (
            <line
              x1={xScale(platformQaLodCt)}
              x2={xScale(platformQaLodCt)}
              y1={0}
              y2={innerHeight}
              stroke={theme.colors.neutral400}
              strokeWidth={1}
              strokeDasharray="2,3"
              shapeRendering="crispEdges"
            />
          )}

          {/* mean */}
          <line
            x1={0}
            x2={innerWidth}
            y1={yScale(meanDelta)}
            y2={yScale(meanDelta)}
            stroke={theme.colors.blue600}
            strokeWidth={1.5}
          />
          {/* +/- 1 SD */}
          <line
            x1={0}
            x2={innerWidth}
            y1={yScale(meanDelta + sdDelta)}
            y2={yScale(meanDelta + sdDelta)}
            stroke={theme.colors.neutral600}
            strokeDasharray="4,4"
          />
          <line
            x1={0}
            x2={innerWidth}
            y1={yScale(meanDelta - sdDelta)}
            y2={yScale(meanDelta - sdDelta)}
            stroke={theme.colors.neutral600}
            strokeDasharray="4,4"
          />
          {/* LoA */}
          <line
            x1={0}
            x2={innerWidth}
            y1={yScale(loaUpper)}
            y2={yScale(loaUpper)}
            stroke={theme.colors.red600}
            strokeDasharray="6,4"
          />
          <line
            x1={0}
            x2={innerWidth}
            y1={yScale(loaLower)}
            y2={yScale(loaLower)}
            stroke={theme.colors.red600}
            strokeDasharray="6,4"
          />

          {/* points */}
          {plottedPoints.map((d, i) => (
            <circle key={i} cx={xScale(d.x)} cy={yScale(d.y)} r={d.r} fill={d.fill} fillOpacity={d.fillOpacity} />
          ))}
        </g>

        {/* border */}
        <rect
          x={0}
          y={0}
          width={innerWidth}
          height={innerHeight}
          rx={8}
          ry={8}
          fill="none"
          stroke={theme.colors.neutral400}
        />

        {/* labels aligned to the right side */}
        <text x={xLabelPos} y={yScale(meanDelta) - 4} fill={theme.colors.blue600} fontSize={11}>
          {`Mean = ${meanDelta.toFixed(2)}`}
        </text>
        <text x={xLabelPos} y={yScale(meanDelta + sdDelta) - 4} fill={theme.colors.neutral700} fontSize={11}>
          {`+1 SD = ${(meanDelta + sdDelta).toFixed(2)}`}
        </text>
        <text x={xLabelPos} y={yScale(meanDelta - sdDelta) + 12} fill={theme.colors.neutral700} fontSize={11}>
          {`-1 SD = ${(meanDelta - sdDelta).toFixed(2)}`}
        </text>
        <text x={xLabelPos} y={yScale(loaUpper) - 4} fill={theme.colors.red700} fontSize={11}>
          {`Upper LOA = ${loaUpper.toFixed(2)}`}
        </text>
        <text x={xLabelPos} y={yScale(loaLower) + 12} fill={theme.colors.red700} fontSize={11}>
          {`Lower LOA = ${loaLower.toFixed(2)}`}
        </text>

        {/* legend */}
        <g>
          <rect
            x={10}
            y={2}
            width={100}
            height={42}
            rx={6}
            ry={6}
            fill={theme.colors.neutral50}
            fillOpacity={0.92}
            stroke={theme.colors.neutral200}
          />
          <circle cx={22} cy={13} r={3} fill={theme.colors.neutral900} />
          <text x={32} y={16} fill={theme.colors.neutral800} fontSize={11}>
            Both Cq &lt; {platformQaLodCt}
          </text>

          <circle cx={22} cy={25} r={3} fill={theme.colors.riskScore100} />
          <text x={32} y={28} fill={theme.colors.neutral800} fontSize={11}>
            One Cq &ge; {platformQaLodCt}
          </text>

          <circle cx={22} cy={37} r={3} fill={theme.colors.neutral500} fillOpacity={0.7} />
          <text x={32} y={40} fill={theme.colors.neutral800} fontSize={11}>
            Both Cq &ge; {platformQaLodCt}
          </text>
        </g>

        {/* axes */}
        <AxisLeft scale={yScale} label={'ΔCq (A − B)'} labelProps={{ fontSize: 12, y: -35 }} />
        <AxisBottom top={innerHeight} scale={xScale} />
        <text x={innerWidth / 2} y={innerHeight + 35} fill={theme.colors.neutral900} fontSize={12} textAnchor="middle">
          Mean Cq of A &amp; B
        </text>
      </Group>
    </svg>
  );
}
